import React from 'react';
import {
	CTAScreen,
	CustomHero,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import { Columns } from '@/components/landing/reusableSections/Columns';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
} from '@/hooks';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import { GenerateLandingLinkService, GoogleService } from '@/services';
import {
	forBusiness_apparel_benefits,
	forBusiness_apparel_eCommerce,
	forBusiness_apparel_getStarted,
	forBusiness_apparel_perfect,
	forBusiness_apparel_quality,
	forBusiness_apparel_tips,
	reviewsLanding,
} from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import eCommerce from '@/public/images/for-business/apparel/eCommerce.webp';
import quality from '@/public/images/for-business/apparel/forBusiness.webp';
import imgHero from '@/public/images/for-business/apparel/hero.webp';
import howToStart from '@/public/images/for-business/apparel/howToStart.webp';
import perfectFor from '@/public/images/for-business/apparel/perfectFor.webp';
import tips from '@/public/images/for-business/apparel/tips.webp';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import styles from './ApparelPhotographyLanding.module.scss';

const ApparelPhotographyLanding: React.FC = () => {
	const { t } = useTranslationWithLocale('apparel-photography', 'en');
	const { t: tReviews } = useTranslationWithLocale('landing-reviews', 'en');

	const goToProduct = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href = GenerateLandingLinkService.webApp();
	};

	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.APPAREL_PHOTOGRAPHY_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.APPAREL_PHOTOGRAPHY },
	);

	useLandingShownEvent({ type: LANDING_AMPLITUDE_EVENTS.APPAREL_PHOTOGRAPHY });
	return (
		<>
			<CustomHero
				image={imgHero}
				title={t('heroData_title')}
				subtitle={t('heroData_subtitle')}
				onClick={goToProduct}
				buttonText={t('getStarted')}
				classes={{
					mainTitle: styles['apparel__hero-title'],
					mainSubtitle: styles['apparel__hero-subtitle'],
					picture: styles['apparel__hero-picture'],
				}}
			/>

			{/* e-commerce */}
			<ImageTextSection
				title={t('eCommerceData_mainTitle')}
				specialListItem
				image={eCommerce}
				data={forBusiness_apparel_eCommerce}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* quality */}
			<ImageTextSection
				title={t('qualityData_mainTitle')}
				subtitle1={t('qualityData_mainSubtitle1')}
				image={quality}
				data={forBusiness_apparel_quality}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['apparel__quality-image-container'],
					list: styles['apparel__quality-list'],
				}}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* Benefits */}
			<Columns
				t={t}
				title={t('benefitsData_mainTitle')}
				data={forBusiness_apparel_benefits}
				buttonText={t('getStarted')}
				onClick={goToProduct}
			/>

			{/* getStarted */}
			<ImageTextSection
				title={t('getStartedData_mainTitle')}
				image={howToStart}
				data={forBusiness_apparel_getStarted}
				buttonText={t('getStarted')}
				extraTextBelow={t('getStartedData_extraText')}
				t={t}
				onClick={goToProduct}
				classes={{
					section: styles['apparel__start-section'],
				}}
			/>

			{/* tips */}
			<ImageTextSection
				title={t('tipsData_mainTitle')}
				image={tips}
				data={forBusiness_apparel_tips}
				buttonText={t('getStarted')}
				extraTextBelow={t('tipsData_extraText')}
				t={t}
				onClick={goToProduct}
				classes={{
					section: styles['apparel__tips-section'],
					imageContainer: styles['apparel__tips-image-container'],
					list: styles['apparel__tips-list'],
				}}
			/>

			{/* perfect */}
			<ImageTextSection
				title={t('perfectData_mainTitle')}
				image={perfectFor}
				data={forBusiness_apparel_perfect}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData_title1')}
				title_green={t('tryLiftData_greenText')}
				title2={t('tryLiftData_title2')}
				subtitle={t('tryLiftData_subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData_altText')}
				buttonText={t('getStarted')}
				onClick={goToProduct}
				classes={{
					title: styles['apparel__tryLift-title'],
					subtitleClass: styles['apparel__tryLift-subtitle'],
				}}
			/>
		</>
	);
};

export default ApparelPhotographyLanding;
